import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { statusOptionsFor } from "modules/appointments/helper/appointmentFilters";
import UserService from "modules/user/service/UserService";
import { PROFILE_TYPE_OPTIONS } from "modules/patient/helper/patientConstants";
import ColumnFilter from "modules/appointments/components/ColumnFilter";
import BottomSheet from "./BottomSheet";

/**
 * Every filter this screen has, in one sheet — Figma 5878:2.
 *
 * Desktop spends a funnel per column plus two id boxes across the top. There is
 * no room for eight popovers at 360px, so they collect here instead. The
 * plumbing underneath is untouched: Apply calls the same `searchBy` and
 * `setColumnFilter` the desktop controls call, with the same values.
 *
 * DRAFT STATE, APPLIED ON DEMAND. Desktop's funnels each apply on their own
 * popover's Apply; here every field is staged locally and committed together,
 * because a sheet that re-queried on each keystroke would reload the list under
 * the fingers of someone still choosing. The draft is re-seeded from the
 * applied values every time the sheet opens, so closing without applying
 * genuinely changes nothing.
 *
 * USER TYPE AND ACCOUNT CODE live here too, added after the board. They were
 * left out first on the grounds that both were long server-searched lists — but
 * only Account code is, and it is the same read the Service field above it
 * already does. User type is a fixed list of five and wants chips, exactly like
 * Status. Every funnel the desktop table draws is now reachable at 360px.
 */
/** "Any service", the one picked service's name, or "N selected". */
const serviceSummary = (ids, options) => {
  if (!ids || !ids.length) return "Any service";
  if (ids.length === 1) {
    const hit = (options || []).find((o) => o.id === ids[0]);
    if (hit) return hit.label;
  }
  return `${ids.length} selected`;
};

const MobileFilterSheet = ({
  open,
  onClose,
  appointmentId,
  patientId,
  status,
  dateRange,
  service,
  serviceOptions,
  onLoadServices,
  loadServices,
  userType,
  accountCode,
  onLoadAccounts,
  onApply,
  onReset,
}) => {
  const [draft, setDraft] = useState({});

  /* The Status chips offer exactly what this user's desktop funnel offers —
     same helper, read the same way AppointmentsDashboard reads it. Apply
     sends these ids straight to setColumnFilter, so the two lists cannot
     drift. Read once: the signed-in user cannot change without a reload. */
  const statusOptions = useMemo(
    () => statusOptionsFor(new UserService().doctorCategory()),
    []
  );

  /* Re-seed from what is actually applied each time the sheet opens, so an
     abandoned edit cannot leak into the next opening. */
  useEffect(() => {
    if (!open) return;
    setDraft({
      appointmentId: appointmentId || "",
      patientId: patientId || "",
      status: status || [],
      from: (dateRange && dateRange.from) || "",
      to: (dateRange && dateRange.to) || "",
      service: service && service.length ? service : null,
      userType: userType || [],
      accountCode: accountCode || null,
    });
  }, [open, appointmentId, patientId, status, dateRange, service, userType, accountCode]);

  /* One unfiltered read when the sheet opens — not for the picker, which
     searches the server itself, but so a single picked service can be shown by
     its name rather than as "1 selected". */
  useEffect(() => {
    if (open && onLoadServices) onLoadServices();
  }, [open, onLoadServices]);


  const set = (k) => (v) => setDraft((prev) => ({ ...prev, [k]: v }));

  /* One toggler for both chip groups — Status and User type differ only in
     which draft key they write. */
  const toggleIn = (key) => (id) =>
    setDraft((prev) => {
      const list = prev[key] || [];
      const on = list.indexOf(id) !== -1;
      return { ...prev, [key]: on ? list.filter((x) => x !== id) : list.concat(id) };
    });

  /* Desktop's date funnel check (ColumnFilter): the server 400s on from > to,
     so Apply is held back with the same message instead. */
  const invalidRange = !!(draft.from && draft.to && draft.from > draft.to);

  const toggleStatus = toggleIn("status");
  const toggleUserType = toggleIn("userType");

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Filters"
      className="zym-sheet-filters"
      footer={
        <>
          <button type="button" className="zym-btn" onClick={onReset}>
            Reset
          </button>
          <button
            type="button"
            className="zym-btn is-primary"
            disabled={invalidRange}
            onClick={() => onApply(draft)}
          >
            Apply filters
          </button>
        </>
      }
    >
      <div className="zym-field-block">
        <label className="zym-label" htmlFor="zym-f-aid">
          Appointment ID
        </label>
        <input
          id="zym-f-aid"
          className="zym-input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 3572"
          maxLength={8}
          value={draft.appointmentId || ""}
          /* Digits only, the same guard the desktop box applies. */
          onChange={(e) => set("appointmentId")(e.target.value.replace(/\D/g, ""))}
        />
      </div>

      <div className="zym-field-block">
        <label className="zym-label" htmlFor="zym-f-pid">
          Patient ID
        </label>
        <input
          id="zym-f-pid"
          className="zym-input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 158426"
          maxLength={8}
          value={draft.patientId || ""}
          onChange={(e) => set("patientId")(e.target.value.replace(/\D/g, ""))}
        />
      </div>

      <div className="zym-field-block">
        <span className="zym-label">Status</span>
        <div className="zym-pillgrid">
          {statusOptions.map((o) => {
            const on = (draft.status || []).indexOf(o.id) !== -1;
            return (
              <button
                key={o.id}
                type="button"
                className={`zym-spill${on ? " is-on" : ""}`}
                aria-pressed={on}
                onClick={() => toggleStatus(o.id)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="zym-field-block">
        <span className="zym-label">Appointment date</span>
        <div className="zym-field-pair">
          <div className="zym-subfield">
            <span className="zym-sublabel">From</span>
            {/* The same bare native field desktop's date funnel draws — no
                min/max. Not common/DatePicker: that is the booking calendar,
                and its default 0–10 day window would clamp a filter that has
                no window at all. dateLayer.js gives it the app's face. */}
            <input
              id="zym-f-from"
              className="zym-input"
              type="date"
              value={draft.from || ""}
              onChange={(e) => set("from")(e.target.value)}
            />
          </div>
          <div className="zym-subfield">
            <span className="zym-sublabel">To</span>
            <input
              id="zym-f-to"
              className="zym-input"
              type="date"
              value={draft.to || ""}
              onChange={(e) => set("to")(e.target.value)}
            />
          </div>
        </div>
        {invalidRange && (
          <p className="zym-field-err" role="alert">
            &ldquo;From&rdquo; is after &ldquo;To&rdquo;.
          </p>
        )}
      </div>

      {/* THE DESKTOP FUNNEL, as Account code below: type to search (server-side,
          debounced — ColumnFilter.js), tick as many as needed, select-all and
          invert across the matches. It was a single-select `Select` over one
          unfiltered page of services, which could neither search the server nor
          pick more than one. Its Apply writes to the draft; "Apply filters"
          sends it with everything else. */}
      <div className="zym-field-block">
        <span className="zym-label">Service name</span>
        <div className="zym-colfilter">
          <span className="zym-colfilter-text">{serviceSummary(draft.service, serviceOptions)}</span>
          <ColumnFilter
            label="Service name"
            mode="checkbox"
            loadOptions={loadServices}
            value={draft.service}
            onApply={(v) => set("service")(v && v.length ? v : null)}
          />
        </div>
      </div>

      {/* A fixed list of five, so chips rather than a picker — the same control
          Status uses above, and the same options the desktop funnel spends
          (PROFILE_TYPE_OPTIONS). Multi-select: the server takes `profileTypes`
          as a list. */}
      <div className="zym-field-block">
        <span className="zym-label">User type</span>
        <div className="zym-pillgrid">
          {PROFILE_TYPE_OPTIONS.map((o) => {
            const on = (draft.userType || []).indexOf(o.id) !== -1;
            return (
              <button
                key={o.id}
                type="button"
                className={`zym-spill${on ? " is-on" : ""}`}
                aria-pressed={on}
                onClick={() => toggleUserType(o.id)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* THE DESKTOP FUNNEL ITSELF. 382 accounts is not a list a phone-shaped
          picker can serve: it needs a search box, select-all across the
          matches, and a count — all of which ColumnFilter already has, and all
          of which were being rebuilt badly here. Mounting it also means the
          search is the SERVER-side one (ColumnFilter.js:91-108), the only kind
          that works on a list longer than one page.

          ITS APPLY WRITES TO THE DRAFT, NOT TO THE SERVER. On desktop each
          funnel commits on its own; here everything is staged and sent together
          by "Apply filters", so onApply sets the draft key and the sheet sends
          it with the rest. Nothing about the popover itself changes. */}
      <div className="zym-field-block">
        <span className="zym-label">Account code</span>
        <div className="zym-colfilter">
          <span className="zym-colfilter-text">
            {draft.accountCode && draft.accountCode.length
              ? `${draft.accountCode.length} selected`
              : "Any account"}
          </span>
          <ColumnFilter
            label="Account code"
            mode="checkbox"
            loadOptions={onLoadAccounts}
            value={draft.accountCode}
            onApply={(v) => set("accountCode")(v && v.length ? v : null)}
          />
        </div>
      </div>

    </BottomSheet>
  );
};

MobileFilterSheet.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  appointmentId: PropTypes.string,
  patientId: PropTypes.string,
  /** Applied status option ids. */
  status: PropTypes.array,
  /** Applied {from,to} as yyyy-mm-dd. */
  dateRange: PropTypes.object,
  /** Applied service ids. */
  service: PropTypes.array,
  serviceOptions: PropTypes.array,
  onLoadServices: PropTypes.func,
  /** The desktop funnel's loader, `(query, done)` — the picker's own search. */
  loadServices: PropTypes.func,
  /** Applied profile-type ids. */
  userType: PropTypes.array,
  /** Applied account codes. */
  accountCode: PropTypes.array,
  /** The dashboard's own account loader, handed straight to ColumnFilter. */
  onLoadAccounts: PropTypes.func,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default MobileFilterSheet;
